import { Container, Row, Col, Card, Button } from 'react-bootstrap'
import { useState, useEffect, useRef, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useAuth } from '../components/auth/useAuth'
import { sendChatbotMessage, clearChatbotMessages, getMyChatbotHistory, restoreChatbotHistoryItem } from '../actions/chatbotActions'
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw'
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition'
import { Capacitor } from '@capacitor/core'
import { SpeechRecognition as NativeSpeechRecognition } from '@capgo/capacitor-speech-recognition'

export default function Chatbot() {
    const { t } = useTranslation()
    const { clientauth } = useAuth()
    const dispatch = useDispatch()

    const chatBodyRef = useRef(null)

    const { loadingChatbot, chatbotmessages, myChatHistory, loadingHistory } = useSelector(state => state.chatbot)

    const [message, setMessage] = useState('')
    const [showHistory, setShowHistory] = useState(false)

    const {
        transcript,
        listening,
        resetTranscript,
        browserSupportsSpeechRecognition
    } = useSpeechRecognition()

    const isNative = Capacitor.isNativePlatform()
    const [nativeListening, setNativeListening] = useState(false)
    const [nativeTranscript, setNativeTranscript] = useState('')
    const nativeTranscriptRef = useRef('')
    const nativeListeningRef = useRef(false)
    const [micDiag, setMicDiag] = useState([])
    const logDiag = useCallback((msg) => {
        setMicDiag((prev) => [...prev.slice(-10), msg])
    }, [])

    useEffect(() => {
        if (!isNative) return
        let handlePartial, handleState, handleError
        const stopNativeUI = () => {
            nativeListeningRef.current = false
            setNativeListening(false)
        }
        Promise.all([
            NativeSpeechRecognition.addListener('partialResults', (data) => {
                const text = data?.matches?.[0] || data?.accumulatedText || ''
                nativeTranscriptRef.current = text
                setNativeTranscript(text)
                logDiag(`parcial -> "${text}"`)
            }),
            NativeSpeechRecognition.addListener('error', (data) => {
                logDiag(`error nativo: ${data?.code || ''} ${data?.message || ''}`.trim())
            }),
            NativeSpeechRecognition.addListener('listeningState', (data) => {
                logDiag(`estado nativo -> ${data?.status}`)
                if (data?.status === 'stopped' && nativeListeningRef.current) {
                    stopNativeUI()
                    setTimeout(() => {
                        const text = (nativeTranscriptRef.current || '').trim()
                        nativeTranscriptRef.current = ''
                        setNativeTranscript('')
                        if (text) {
                            logDiag(`corte solo, envío -> "${text}"`)
                            dispatch(sendChatbotMessage(text, chatbotmessages))
                        } else {
                            logDiag('corte solo SIN texto (¿silencio o fallo del recognizador?)')
                        }
                    }, 500)
                }
            })
        ]).then(([h1, h2, h3]) => { handlePartial = h1; handleError = h2; handleState = h3 })
        return () => { handlePartial?.remove?.(); handleState?.remove?.(); handleError?.remove?.() }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isNative, logDiag])

    const showMic = isNative || browserSupportsSpeechRecognition
    const micActive = isNative ? (nativeListening || listening) : listening
    const micTranscript = isNative ? (nativeTranscript || transcript) : transcript

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'instant',
        })
    }, [dispatch, t])

    useEffect(() => {
        if (clientauth) {
            dispatch(getMyChatbotHistory())
        }
    }, [dispatch, clientauth])

    useEffect(() => {
        if (chatBodyRef.current) {
            chatBodyRef.current.scrollTo({
                top: chatBodyRef.current.scrollHeight,
                behavior: 'smooth'
            })
        }
    }, [chatbotmessages, loadingChatbot])

    const wasListening = useRef(false)

    useEffect(() => {
        if (wasListening.current && !listening && transcript.trim()) {
            dispatch(sendChatbotMessage(transcript.trim(), chatbotmessages))
            resetTranscript()
        }
        wasListening.current = listening
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listening])

    const handleSendMessage = () => {
        const text = message || micTranscript
        if (!text || !text.trim()) return

        dispatch(sendChatbotMessage(text.trim(), chatbotmessages))
        setMessage('')
        resetTranscript()
    }

    const handleToggleHistory = () => {
        const next = !showHistory
        setShowHistory(next)
        if (next) dispatch(getMyChatbotHistory())
    }

    const handleRestore = (log) => {
        dispatch(restoreChatbotHistoryItem(log))
    }

    const handleMicClick = useCallback(async () => {
        if (isNative) {
            if (nativeListening) {
                logDiag('--- toque: parar ---')
                nativeListeningRef.current = false
                try {
                    await NativeSpeechRecognition.stop()
                } catch (e) { logDiag(`stop falló: ${e?.message || e}`) }
                await new Promise((r) => setTimeout(r, 600))
                setNativeListening(false)
                const text = (nativeTranscriptRef.current || '').trim()
                nativeTranscriptRef.current = ''
                setNativeTranscript('')
                if (text) {
                    logDiag(`envío manual -> "${text}"`)
                    dispatch(sendChatbotMessage(text, chatbotmessages))
                } else {
                    logDiag('parada manual SIN texto capturado')
                }
                return
            }
            logDiag('--- toque: empezar ---')
            try {
                const { available } = await NativeSpeechRecognition.available()
                logDiag(`disponible: ${available}`)
                if (!available) throw new Error('reconocedor no disponible')
                let status = await NativeSpeechRecognition.checkPermissions()
                logDiag(`permiso voz: ${status?.speechRecognition}`)
                if (status?.speechRecognition !== 'granted') {
                    status = await NativeSpeechRecognition.requestPermissions()
                    logDiag(`tras pedir permiso: ${status?.speechRecognition}`)
                }
                if (status?.speechRecognition !== 'granted') {
                    logDiag('PERMISO DENEGADO, aborto')
                    return
                }
                nativeTranscriptRef.current = ''
                setNativeTranscript('')
                try {
                    await NativeSpeechRecognition.start({ language: 'es-ES', partialResults: true, maxResults: 1 })
                    logDiag('start: OK (esperando audio...)')
                } catch (se) {
                    logDiag(`start FALLÓ: ${se?.message || se}`)
                    throw se
                }
                nativeListeningRef.current = true
                setNativeListening(true)
            } catch (e) {
                logDiag(`error: ${e?.message || e}`)
                setNativeListening(false)
                if (browserSupportsSpeechRecognition) {
                    logDiag('fallback: voz web')
                    SpeechRecognition.startListening({ continuous: false, language: 'es-ES' })
                }
            }
            return
        }
        if (!browserSupportsSpeechRecognition) return
        if (listening) {
            SpeechRecognition.stopListening()
        } else {
            SpeechRecognition.startListening({ continuous: false, language: 'es-ES' })
        }
    }, [isNative, nativeListening, listening, browserSupportsSpeechRecognition, dispatch, chatbotmessages, logDiag])

    return (
        <Container fluid className='contenido p-3 p-lg-5'>
            <Row>
                <Col>
                    <Card>
                        <Card.Header className="bg-white">
                            <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
                                <div>
                                    <h4 className="mb-0">{t('chatbot.title')}</h4>
                                    <small className="text-muted">
                                        {clientauth?.user?.name || ''}
                                    </small>
                                </div>
                                <div className="d-flex gap-2 flex-shrink-0 flex-wrap">
                                    <Button
                                        variant={showHistory ? 'primary' : 'outline-primary'}
                                        size="sm"
                                        onClick={handleToggleHistory}
                                    >
                                        <i className="fas fa-history me-1"></i>
                                        {t('chatbot.history')}
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => dispatch(clearChatbotMessages())}
                                    >
                                        <i className="fas fa-trash-alt me-1"></i>
                                        {t('chatbot.clear')}
                                    </Button>
                                </div>
                            </div>
                        </Card.Header>

                        <Card.Body
                            ref={chatBodyRef}
                            className="p-3 bg-light chatbot-body"
                            style={{ height: '55vh', overflowY: 'auto' }}
                        >
                            {!chatbotmessages?.length && !loadingChatbot && (
                                <div className="chatbot-messages received">
                                    {t('chatbot.welcome')}
                                </div>
                            )}

                            {chatbotmessages?.map((item, index) => (
                                <div
                                    key={index}
                                    className={`chatbot-messages ${item.role} ${item.role === 'user' ? 'send' : 'received'}`}
                                >
                                    <ReactMarkdown rehypePlugins={[rehypeRaw]}>{item.content}</ReactMarkdown>
                                </div>
                            ))}

                            {loadingChatbot && (
                                <div className="chatbot-messages loading">
                                    {t('chatbot.loading')}
                                </div>
                            )}
                        </Card.Body>

                        <Card.Footer className="bg-white">
                            <div className="d-flex gap-2 align-items-start">
                                <div className="flex-grow-1">
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !loadingChatbot) {
                                                handleSendMessage()
                                            }
                                        }}
                                        placeholder={t('chatbot.placeholder') || 'Escribe tu consulta'}
                                        disabled={loadingChatbot || micActive}
                                    />
                                    {showMic && (
                                        <i
                                            className={`fas fa-microphone mt-2 ${micActive ? 'text-danger' : 'text-muted'}`}
                                            onClick={handleMicClick}
                                            style={{ cursor: 'pointer', fontSize: 18 }}
                                        ></i>
                                    )}
                                    {isNative && micDiag.length > 0 && (
                                        <div className="mt-1 text-muted" style={{ fontSize: 11, whiteSpace: 'pre-wrap' }}>
                                            {micDiag.map((line, i) => (
                                                <div key={i}>{line}</div>
                                            ))}
                                        </div>
                                    )}
                                    {micActive && micTranscript && (
                                        <div className="text-muted small mt-1">
                                            <i className="fas fa-microphone-alt me-1 text-danger"></i>
                                            {micTranscript}
                                        </div>
                                    )}
                                </div>
                                <Button
                                    variant="primary"
                                    onClick={handleSendMessage}
                                    disabled={loadingChatbot || micActive || !(message.trim() || micTranscript.trim())}
                                    className="flex-shrink-0"
                                    style={{ height: 48, minWidth: 48, padding: '0 14px' }}
                                >
                                    <i className="fas fa-paper-plane"></i>
                                </Button>
                            </div>
                        </Card.Footer>
                    </Card>
                </Col>
                {showHistory && (
                    <Col xs={12} lg={4} className="mt-3 mt-lg-0">
                        <Card>
                            <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                                <strong>{t('chatbot.myHistory')}</strong>
                                <Button variant="link" size="sm" onClick={() => dispatch(getMyChatbotHistory())}>
                                    <i className="fas fa-sync-alt"></i>
                                </Button>
                            </Card.Header>
                            <Card.Body style={{ height: '55vh', overflowY: 'auto' }}>
                                {loadingHistory ? (
                                    <div className="text-muted small">{t('chatbot.loading')}</div>
                                ) : !myChatHistory?.length ? (
                                    <div className="text-muted small">{t('chatbot.noHistory')}</div>
                                ) : (
                                    myChatHistory.map((log) => (
                                        <div key={log._id} className="border rounded p-2 mb-2">
                                            <div className="small text-muted">
                                                {log.createdAt ? new Date(log.createdAt).toLocaleString('es-ES') : ''}
                                            </div>
                                            <div className="fw-bold small mt-1">
                                                <i className="fas fa-user me-1"></i>{log.question}
                                            </div>
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="p-0 mt-1"
                                                onClick={() => handleRestore(log)}
                                            >
                                                {t('chatbot.restore')}
                                            </Button>
                                        </div>
                                    ))
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                )}
            </Row>
        </Container>
    )
}